import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { UserSchema } from '../entities/user';
import { updateUser } from '../queries/__mocked_data__/users-state';

export namespace EditUserMutation {
  export type Input = {
    id: string;
    active: boolean;
    firstName: string;
    lastName: string;
    email: string;
    roleId: string;
  };

  export const OutputSchema = z.object({
    user: UserSchema.pick({ id: true }),
  });
  export type Output = z.infer<typeof OutputSchema>;

  function useMutationFn() {
    return async function mutationFn(input: Input): Promise<Output> {
      // Add artificial 500ms delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      const result = updateUser(input.id, {
        active: input.active,
        firstName: input.firstName,
        lastName: input.lastName,
        email: input.email,
        roleId: input.roleId,
      });

      if (!result) {
        throw new Error(`Failed to update user with id ${input.id}`);
      }

      return result;
    };
  }

  export function useMutation() {
    const queryClient = rq.useQueryClient();
    const mutationFn = useMutationFn();

    return rq.useMutation<Output, unknown, Input>({
      mutationFn,
      async onSuccess({ user }) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['user-management', 'users'],
          }),
          queryClient.invalidateQueries({
            queryKey: ['user-management', 'user', user.id],
          }),
          queryClient.invalidateQueries({
            queryKey: ['user-management', 'roles'],
          }),
        ]);
      },
    });
  }
}
